import { ApolloError, useMutation } from '@apollo/client';
import { RateCardCreateModelInput, RateCardModel, RateCardPatchModelInput } from '@utmost.co/enterprise-models';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import CreateRateCard from 'src/components/pages/frontdoor/forms/AddRateCard/CREATE_RATE_CARD';
import DeleteRateCard from 'src/components/pages/frontdoor/forms/AddRateCard/DELETE_RATE_CARD';
import UpdateRateCard from 'src/components/pages/frontdoor/forms/AddRateCard/UPDATE_RATE_CARD';
import useSystemLoading from 'src/utils/hooks/useSystemLoading';

interface UpdateParams {
  rateCard: Partial<RateCardPatchModelInput>;
  uid: string;
}

interface CreateParams {
  rateCard: Partial<RateCardCreateModelInput>;
}

interface Output {
  rateCards?: RateCardModel[];
  update(params: UpdateParams): Promise<Partial<RateCardModel> | undefined>;
  create(params: CreateParams): Promise<Partial<RateCardModel> | undefined>;
  remove(uid: string, onDelete?: (uids: string[]) => void): Promise<void>;
  loading: boolean;
}

export const useRateCardData = (): Output => {
  const { showSuccess, showError } = useSnackbar();

  const onError = (error: ApolloError) => {
    showError(error.message);
  };

  const [createRateCard, { loading: createLoading }] = useMutation<
    { createRateCard: Partial<RateCardModel | RateCardModel> },
    CreateParams
  >(CreateRateCard || '', { onError });

  const [updateRateCard, { loading: updateLoading }] = useMutation<
    { createRateCard: Partial<RateCardModel | RateCardModel> },
    UpdateParams
  >(UpdateRateCard || '', { onError });

  const [removeRateCard, { loading: removeLoading }] = useMutation<void, { uid: string }>(DeleteRateCard);

  useSystemLoading(createLoading || updateLoading || removeLoading);

  const update = async (params: UpdateParams) => {
    const { data } = await updateRateCard({
      variables: structuredClone(params)
    });
    showSuccess('settings.engagements.rateCard.update.success');
    return data?.createRateCard;
  };

  const create = async (params: CreateParams) => {
    const { data } = await createRateCard({
      variables: structuredClone(params)
    });
    showSuccess('settings.engagements.rateCard.create.success');
    return data?.createRateCard;
  };

  const remove = async (uid: string) => {
    await removeRateCard({ variables: { uid } });
    showSuccess('settings.engagements.rateCard.remove.success');
  };

  return {
    create,
    update,
    remove,
    loading: createLoading || updateLoading || removeLoading
  };
};
